import { useEffect, useRef, useState } from "react";
import { translateError, useT } from "../i18n/hooks.ts";
import {
  findDateRowIndex,
  formatScaleDate,
  parseScaleDateField,
  pasteFormatKey,
  type SeriesScale,
} from "../model/seriesScale.ts";
import { SearchIcon } from "./SearchIcon.tsx";

/**
 * Kept in lockstep with `.series-table tbody tr`'s fixed height
 * (editBar.css) — the spacer rows below use it to reserve the right total
 * scroll height for rows that aren't actually rendered.
 */
const ROW_HEIGHT = 30;
/** Matches `.series-table-wrap .table-scroll`'s own max-height (editBar.css) — roughly 8 rows, same footprint as the chart view next to it. */
const VISIBLE_ROWS = 8;
const OVERSCAN = 10;

/**
 * Table view of one series column — the alternative to PasteDailySeries.tsx's
 * chart, for fixing a single day's value directly instead of copying the
 * whole series out, editing it in a spreadsheet, and pasting it all back in
 * through "Sustituir por otra serie…".
 *
 * Windowed by hand (no pagination, no library — plain fixed-row-height
 * math): a daily series over the full ROCIO record is ~26,300 rows, far too
 * many to mount as real DOM rows at once, but pagination made finding a
 * given date a hunt-by-page-number exercise — the user explicitly asked for
 * a single continuous scroll instead. Two blank spacer `<tr>`s (their own
 * `height` reserving the space of everything above/below the rendered
 * window) keep the scrollbar's real proportions without mounting more than
 * `VISIBLE_ROWS + 2*OVERSCAN` rows at once.
 *
 * The date search box (goToDate) is a NAVIGATION aid, not a filter — the
 * user was explicit about this: typing a date scrolls the window to it
 * (findDateRowIndex, seriesScale.ts — exact match, or the first date after
 * if there's no exact one), it never hides any row. Focuses the found row's
 * value input once it's actually mounted in the window (inputRefs +
 * the effect below) — scrolling lands it there, but React hasn't rendered
 * that row yet in the same tick, so the focus has to wait for the width
 * window to actually include it.
 *
 * Each visible value is its own uncontrolled input (`defaultValue`, not
 * `value`) — committed on blur/Enter, not per keystroke: `onChangeValue`
 * rebuilds the WHOLE column's Float64Array (PasteDailySeries.tsx calls
 * dailySeries.ts's `updateSeriesValue`, indexed by `colIndex` — NOT by
 * column name, see that function's own comment for why), and doing that on
 * every single keystroke while typing a number would be needless copying
 * for no visible benefit. `key={date+value}` forces the input to remount
 * (picking up a fresh `defaultValue`) if the underlying value ever changes
 * from outside this table (e.g. the whole series gets replaced via
 * "Sustituir por otra serie…" while this same page is open).
 */
export function SeriesTable({
  dates,
  values,
  scale,
  onChangeValue,
}: {
  dates: string[];
  values: Float64Array;
  scale: SeriesScale;
  onChangeValue: (dateIndex: number, value: number) => void;
}) {
  const t = useT();
  const [scrollTop, setScrollTop] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const [searchText, setSearchText] = useState("");
  const [searchError, setSearchError] = useState<string | null>(null);
  const [focusIndex, setFocusIndex] = useState<number | null>(null);
  const inputRefs = useRef(new Map<number, HTMLInputElement>());

  const viewportHeight = ROW_HEIGHT * VISIBLE_ROWS;
  const startIndex = Math.max(0, Math.floor(scrollTop / ROW_HEIGHT) - OVERSCAN);
  const endIndex = Math.min(
    dates.length,
    Math.ceil((scrollTop + viewportHeight) / ROW_HEIGHT) + OVERSCAN
  );

  useEffect(() => {
    if (focusIndex === null || focusIndex < startIndex || focusIndex >= endIndex) return;
    const input = inputRefs.current.get(focusIndex);
    if (input) {
      input.focus();
      input.select();
    }
    setFocusIndex(null);
  }, [focusIndex, startIndex, endIndex]);

  function commit(dateIndex: number, raw: string) {
    const trimmed = raw.trim();
    onChangeValue(dateIndex, trimmed === "" ? NaN : Number(trimmed));
  }

  function goToDate() {
    setSearchError(null);
    let targetIso: string;
    try {
      targetIso = parseScaleDateField(searchText, scale);
    } catch (err) {
      setSearchError(translateError(err, t));
      return;
    }
    const index = findDateRowIndex(dates, targetIso);
    // Centered, not top-aligned — landing the row right at the edge of the
    // viewport reads as "barely found it"; a bit of context above/below
    // makes it obvious the search actually worked.
    const centered = index * ROW_HEIGHT - Math.floor((VISIBLE_ROWS - 1) / 2) * ROW_HEIGHT;
    containerRef.current?.scrollTo({ top: Math.max(0, centered) });
    setFocusIndex(index);
  }

  return (
    <div className="series-table-wrap">
      <div className="series-table-search">
        <input
          type="text"
          className="series-table-search-input"
          placeholder={t(pasteFormatKey(scale))}
          value={searchText}
          onChange={(e) => {
            setSearchText(e.target.value);
            setSearchError(null);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              goToDate();
            }
          }}
        />
        <button
          type="button"
          className="button-icon"
          title={t("pasteSeries.goToDate")}
          onClick={goToDate}
        >
          <SearchIcon />
        </button>
      </div>
      {searchError && <p className="error">{searchError}</p>}
      <div
        className="table-scroll"
        ref={containerRef}
        onScroll={(e) => setScrollTop(e.currentTarget.scrollTop)}
      >
        <table className="series-table">
          <thead>
            <tr>
              <th>{t("common.date")}</th>
              <th>{t("pasteSeries.value")}</th>
            </tr>
          </thead>
          <tbody>
            {startIndex > 0 && (
              <tr aria-hidden="true" style={{ height: startIndex * ROW_HEIGHT }}>
                <td colSpan={2} />
              </tr>
            )}
            {Array.from({ length: endIndex - startIndex }, (_, i) => {
              const dateIndex = startIndex + i;
              const date = dates[dateIndex];
              const value = values[dateIndex];
              return (
                <tr key={date}>
                  <td>{formatScaleDate(date, scale)}</td>
                  <td>
                    <input
                      key={`${date}:${value}`}
                      ref={(el) => {
                        if (el) inputRefs.current.set(dateIndex, el);
                        else inputRefs.current.delete(dateIndex);
                      }}
                      type="number"
                      step="any"
                      defaultValue={Number.isNaN(value) ? "" : value}
                      placeholder={t("pasteSeries.noValue")}
                      onBlur={(e) => commit(dateIndex, e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") e.currentTarget.blur();
                      }}
                    />
                  </td>
                </tr>
              );
            })}
            {endIndex < dates.length && (
              <tr aria-hidden="true" style={{ height: (dates.length - endIndex) * ROW_HEIGHT }}>
                <td colSpan={2} />
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
